import { useEffect, useState } from 'react';

import { LOCALSTORAGE_PREFIX } from 'config/config.types';
import { isNotNullOrUndefined, isNullOrUndefined, isStringNotBlank } from 'utils/utils';
import { getFromLocalStorage, setInLocalStorage } from 'utils/storageUtils';

// Custom hook to detect if the current tab has multiple tabs open by resource id
export const useDetectMultipleTabs = (resourceId?: string): boolean => {
  const [hasMultipleTabs, setHasMultipleTabs] = useState<boolean>(false);
  const [localSessionStorage, setLocalSessionStorage] = useState<Record<string, string> | null>(null);

  useEffect(() => {
    if (!isStringNotBlank(resourceId)) {
      setHasMultipleTabs(false);
      return;
    }

    const localStorageKey = `${LOCALSTORAGE_PREFIX}activeTabs-${resourceId}`;
    const sessionStorageKey = `${LOCALSTORAGE_PREFIX}tabIdentifier-${resourceId}`;
    let currentTabIdentifier = isNotNullOrUndefined(localSessionStorage)
      ? localSessionStorage[sessionStorageKey]
      : null;

    // Generate a new unique identifier
    const generateTabIdentifier = () => `${LOCALSTORAGE_PREFIX}tab-${Date.now()}`;

    // If no identifier is found in session storage, or the identifier already exists in local storage, regenerate it
    const ensureUniqueTabIdentifier = () => {
      const activeTabs: string[] = getFromLocalStorage<string[]>(localStorageKey) ?? [];
      if (isNullOrUndefined(currentTabIdentifier) || activeTabs.includes(currentTabIdentifier)) {
        currentTabIdentifier = generateTabIdentifier();
        // sessionStorage.setItem(sessionStorageKey, currentTabIdentifier);
        setLocalSessionStorage({ sessionStorageKey: currentTabIdentifier });

        // Update the active tabs list
        activeTabs.push(currentTabIdentifier);
        setInLocalStorage(localStorageKey, activeTabs);
      }
    };

    ensureUniqueTabIdentifier();

    // Function to check if there are multiple active tabs
    const checkMultipleTabs = () => {
      const activeTabs = getFromLocalStorage<string[]>(localStorageKey) ?? [];
      setHasMultipleTabs(activeTabs.length > 1);
    };

    // Initial check when the hook mounts
    checkMultipleTabs();

    // Listen for changes in localStorage to handle real-time updates
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === localStorageKey) {
        checkMultipleTabs();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Cleanup function to remove the tab identifier from sessionStorage and localStorage
    const handleBeforeUnload = () => {
      // Get the active tabs from localStorage
      const activeTabs = getFromLocalStorage<string[]>(localStorageKey) ?? [];

      // Remove the current tab identifier
      const updatedTabs = activeTabs.filter((tabId) => tabId !== currentTabIdentifier);

      // Update the localStorage with the new list of active tabs
      setInLocalStorage(localStorageKey, updatedTabs);

      // Remove the session storage entry for this tab
      setLocalSessionStorage(null);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup on component unmount
    return () => {
      handleBeforeUnload();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [resourceId]);

  return hasMultipleTabs;
};
